/**
 * Position Ordering — show 4 distinct positions, ask user to identify
 * the widest or tightest among them.
 *
 * Pulls from 4 width tiers encoded in `preflopRanges.ts`:
 *   EARLY (UTG/UTG+1/UTG+2)  → tightest
 *   MIDDLE (MP/HJ/CO)        → medium
 *   SB                       → wide
 *   BTN                      → widest
 *
 * Each question picks one EARLY position, one MIDDLE position, then
 * includes both SB and BTN. That guarantees 4 distinct range sizes, so
 * the "tightest" and "widest" answers are unambiguous. The randomized
 * choice of which EARLY / MIDDLE position appears keeps the surface
 * fresh across reps.
 */

import {
  POSITIONS,
  type Position,
} from "@/features/quiz/lib/preflopRanges";

import { pickFrom, shuffle } from "@/features/quiz/lib/common";

const EARLY: Position[] = ["UTG", "UTG+1", "UTG+2"];
const MIDDLE: Position[] = ["MP", "HJ", "CO"];

export type Prompt = "tightest" | "widest";

export interface PositionOrderingQuestion {
  positions: Position[]; // 4 positions in display order (shuffled)
  prompt: Prompt;
  /** Position that satisfies the prompt. */
  answer: Position;
}

export function generateQuestion(
  rand: () => number = Math.random,
): PositionOrderingQuestion {
  const earlyPick = pickFrom(EARLY, rand);
  const middlePick = pickFrom(MIDDLE, rand);
  const positions: Position[] = shuffle(
    [earlyPick, middlePick, "SB", "BTN"],
    rand,
  );
  const prompt: Prompt = rand() < 0.5 ? "tightest" : "widest";
  const answer: Position = prompt === "tightest" ? earlyPick : "BTN";
  return { positions, prompt, answer };
}

// Exposed so tests can verify constants stay coherent with preflopRanges
export const _internals = { EARLY, MIDDLE, POSITIONS };
